import React, {useRef, useState} from 'react';
import {TouchableWithoutFeedback, View} from 'react-native';
import Modal from 'react-native-modal';

import Icon from '/component/base/Icon';
import {styles as radioStyles} from '/component/base/RadioButton/RadioButton.style';
import Text from '/component/base/Text';
import TextInput from '/component/base/TextInput';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {SearchAndFilter} from '/constant/TestIDs';
import VolumePerformanceTestIDs from '/constant/TestIDs/VolumePerformance';
import {onyx} from '/theme/color/on.color';

import {PERIODS} from '../constants/volumePerformanceReport.constants';
import {styles} from '../styles/VolPerfSearchHeader.style';
import {
  MonthOption,
  VolPerfSearchHeaderProps,
} from '../types/VolPerfComponent.types';

const getMonthName = (monthOffset: number): string => {
  const date = new Date();
  date.setMonth(date.getMonth() + monthOffset);
  return new Intl.DateTimeFormat('en-US', {month: 'short'}).format(date);
};

export const VolPerfSearchHeader = ({
  onMonthSelect,
  onSearchChange,
  selectedPeriod = PERIODS.CURRENT_MONTH,
  activeTab = 'orders',
  searchValue = '',
}: VolPerfSearchHeaderProps) => {
  const [menuVisible, setMenuVisible] = useState(false);

  let options: MonthOption[] = [
    {
      key: PERIODS.LAST_MONTH,
      label: `Last Month (${getMonthName(-1)})`,
      value: `Last Month (${getMonthName(-1)})`,
    },
    {
      key: PERIODS.CURRENT_MONTH,
      label: `Current Month (${getMonthName(0)})`,
      value: `Current Month (${getMonthName(0)})`,
    },
    {
      key: PERIODS.NEXT_MONTH,
      label: `Next Month (${getMonthName(1)})`,
      value: `Next Month (${getMonthName(1)})`,
    },
  ];

  if (activeTab === 'orders') {
    options = options.filter(option => option.key !== PERIODS.NEXT_MONTH);
  }

  const selectedMonth =
    options.find(opt => opt.key === selectedPeriod) || options[0];

  const openMenu = () => {
    if (buttonRef.current) {
      buttonRef.current.measureInWindow(
        (x: number, y: number, width: number, height: number) => {
          setMenuPos({
            top: y + height,
            left: x,
            width: width,
          });
          setMenuVisible(true);
        },
      );
    }
  };

  const handleSelect = (option: MonthOption) => {
    onMonthSelect?.(option);
    setMenuVisible(false);
  };

  const [menuPos, setMenuPos] = useState({top: 0, left: 0, width: 0});
  const buttonRef: any = useRef(null);

  return (
    <View style={styles.headerWrapper}>
      <View style={styles.container}>
        {/* Dropdown Button */}
        <TouchableOpacity
          ref={buttonRef}
          style={styles.dropdownButton}
          onPress={openMenu}
          accessible={true}
          accessibilityLabel={`Selected month: ${selectedMonth?.value}`}
          accessibilityRole="combobox"
          accessibilityState={{expanded: menuVisible}}
          testID={VolumePerformanceTestIDs.volPerfMonthDropDown}>
          <Icon name="calendar" accessible={false} style={styles.icon} />
          <View style={styles.textPadding}>
            <Text style={styles.text} isBold numberOfLines={1}>
              {selectedMonth?.value}
            </Text>
          </View>
          <Icon name="chevron-down" style={styles.chevron} />
        </TouchableOpacity>

        <Modal
          isVisible={menuVisible}
          animationIn="fadeIn"
          animationOut="fadeOut"
          animationInTiming={150}
          animationOutTiming={150}
          backdropOpacity={0}
          onBackdropPress={() => setMenuVisible(false)}
          onBackButtonPress={() => setMenuVisible(false)}
          style={{margin: 0}} // Ensure modal takes full screen for absolute positioning
        >
          {/* Backdrop */}
          <TouchableWithoutFeedback onPress={() => setMenuVisible(false)}>
            <View style={styles.overlay} />
          </TouchableWithoutFeedback>

          {/* Dropdown */}
          <View
            style={[
              styles.menu,
              {
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
              },
            ]}
            accessibilityRole="menu"
            testID={VolumePerformanceTestIDs.volPerfMonthMenu}>
            {options.map(option => {
              const isSelected = selectedMonth?.key === option.key;
              return (
                <TouchableOpacity
                  key={option.key}
                  style={[styles.menuItem, {width: menuPos.width}]}
                  onPress={() => handleSelect(option)}
                  testID={VolumePerformanceTestIDs.volPerfMonthMenuOption}
                  accessible={true}
                  accessibilityLabel={option.value}
                  accessibilityRole="menuitem"
                  accessibilityState={{selected: isSelected}}>
                  <View style={styles.itemContent}>
                    <View style={styles.radioRow}>
                      <View
                        style={
                          isSelected
                            ? [
                                radioStyles.radioButtonSelected,
                                styles.radioIcon,
                              ]
                            : [radioStyles.radioButton, styles.radioIcon]
                        }
                      />
                      <Text
                        style={
                          isSelected
                            ? [
                                radioStyles.radioButtonTextSelected,
                                styles.radioLabelSelected,
                              ]
                            : [radioStyles.radioButtonText, styles.radioLabel]
                        }>
                        {option.value}
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </Modal>

        {/* Search Input Box */}
        <View
          style={styles.searchContainer}
          testID={SearchAndFilter.searchInput}>
          <Icon name="search" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search"
            placeholderTextColor={onyx}
            value={searchValue}
            onChangeText={onSearchChange}
            accessible={true}
            accessibilityLabel="Search input"
          />
        </View>
      </View>
    </View>
  );
};

export default VolPerfSearchHeader;
